import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import Button from './Button';
import Select from './Select';
import TextInput from './TextInput';
import Dialog from '../layout/Dialog';
import Row from '../layout/Row';
import Column from '../layout/Column';
import ErrorText from '../layout/ErrorText';
import NotificationService from '../services/NotificationService';
import { isValidImageSize } from '../validations';

const maxCropDimension = 4096;

const presets = [
  { width: 960, height: 640 },
  { width: 279, height: 327 },
  { width: 336, height: 248 },
  { width: 312, height: 312 },
];

const ImageCropDialog = ({ file, cropSize, onConfirm, onCancel }) => {
  const cropperRef = useRef();
  const [src, setSrc] = useState(null);
  const [size, setSize] = useState(cropSize);
  const [isCustomSize, setIsCustomSize] = useState(false);
  const [selection, setSelection] = useState({ width: 0, height: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setSrc(url);
    image.onerror = () => {
      NotificationService.addNotification('Unable to load the image. Please select another file.', 'error');
      onCancel();
    };
    image.src = url;
    return () => {
      image.onload = null;
      image.onerror = null;
      URL.revokeObjectURL(url);
    };
  }, [file, onCancel]);

  const dimensions = size || selection;
  const isValidDimension = (value) => Number.isInteger(value) && value > 0 && value <= maxCropDimension;
  const hasValidSize = isValidDimension(dimensions.width) && isValidDimension(dimensions.height);

  const handleCropError = () => {
    setSaving(false);
    NotificationService.addNotification('Unable to crop the image. Please try again.', 'error');
  };

  const handleConfirm = () => {
    if (!hasValidSize || saving) {
      return;
    }
    setSaving(true);
    try {
      const canvas = cropperRef.current.getCroppedCanvas({
        ...(size || {}),
        imageSmoothingQuality: 'high',
      });
      if (!canvas) {
        handleCropError();
        return;
      }
      canvas.toBlob((blob) => {
        setSaving(false);
        if (!blob) {
          handleCropError();
          return;
        }
        if (!isValidImageSize(blob)) {
          NotificationService.addNotification(
            'Only images smaller than 1MB can be uploaded. Reduce the crop size and try again.',
            'error'
          );
          return;
        }
        onConfirm(new File([blob], file.name, { type: blob.type }));
      }, file.type);
    } catch (error) {
      handleCropError();
    }
  };

  return (
    <Dialog
      isOpen
      title="Crop image"
      width={760}
      maskClosable={false}
      closeable={!saving}
      onCancel={saving ? null : onCancel}
      actions={[
        <Button key="cancel" label="Cancel" disabled={saving} onClick={onCancel} />,
        <Button
          key="confirm"
          label="Confirm crop"
          type="primary"
          loading={saving}
          disabled={!selection.width || !selection.height || !hasValidSize}
          onClick={handleConfirm}
        />,
      ]}
    >
      <Select
        label="Crop size (width × height)"
        allowClear={false}
        disabled={saving}
        value={size ? (isCustomSize ? 'custom' : `${size.width}x${size.height}`) : 'free'}
        options={[
          ...presets.map((preset) => ({
            value: `${preset.width}x${preset.height}`,
            label: `${preset.width} × ${preset.height} px${
              preset.width === cropSize.width && preset.height === cropSize.height ? ' (recommended)' : ''
            }`,
          })),
          { value: 'custom', label: 'Custom size' },
          { value: 'free', label: 'Free selection' },
        ]}
        onChange={(value) => {
          setIsCustomSize(value === 'custom');
          setSize(
            value === 'custom'
              ? size || selection
              : presets.find((preset) => `${preset.width}x${preset.height}` === value) || null
          );
        }}
      />
      {size && isCustomSize && (
        <Row>
          {['width', 'height'].map((dimension) => (
            <Column key={dimension} size={6}>
              <TextInput
                label={dimension === 'width' ? 'Width (px)' : 'Height (px)'}
                type="number"
                minNumberValue={1}
                maxNumberValue={maxCropDimension}
                disabled={saving}
                value={size[dimension]}
                errorText={
                  isValidDimension(size[dimension]) ? null : `Enter a whole number between 1 and ${maxCropDimension}.`
                }
                onChange={(value) => {
                  setIsCustomSize(true);
                  setSize({ ...size, [dimension]: value === '' ? '' : Number(value) });
                }}
              />
            </Column>
          ))}
        </Row>
      )}
      {src && (
        <Cropper
          ref={cropperRef}
          src={src}
          style={{ height: 400, width: '100%' }}
          aspectRatio={size && hasValidSize ? size.width / size.height : 0}
          viewMode={1}
          background={false}
          crop={(event) =>
            setSelection({ width: Math.floor(event.detail.width), height: Math.floor(event.detail.height) })
          }
        />
      )}
      <div aria-live="polite" style={{ marginTop: 12 }}>
        {dimensions.width} × {dimensions.height} px (width × height)
      </div>
      {!size && (selection.width > maxCropDimension || selection.height > maxCropDimension) && (
        <ErrorText>Crop dimensions must not exceed {maxCropDimension} px per side.</ErrorText>
      )}
    </Dialog>
  );
};

ImageCropDialog.propTypes = {
  file: PropTypes.instanceOf(File).isRequired,
  cropSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ImageCropDialog;
